/**
 * Who is signed in, and which clinic they are working at.
 *
 * The access token lives in memory only. It never reaches storage, so a script
 * that runs in this page cannot read it back after a reload, and it disappears
 * when the tab closes.
 *
 * The refresh token lives in sessionStorage, which is the compromise. Holding
 * it in memory too would sign a doctor out on every page reload. sessionStorage
 * is per-tab and clears when the tab closes, so a shared clinic computer does
 * not carry a session to the next person. It is still readable by any script on
 * this origin, which is the reason RAID I-09 exists: before real patients, the
 * refresh token belongs in an httpOnly cookie that the browser will not show to
 * JavaScript at all.
 *
 * The active clinic is the other half of a portal session (ADR-008). Every
 * /portal call carries it, and the server checks it against the caller's
 * assignments. Changing it here changes what the caller asks for, never what
 * they are allowed.
 */

const REFRESH_KEY = "healtrics.refresh";
const CLINIC_KEY = "healtrics.clinic";
// Who is signed in, so the portal can address a person rather than a
// building. Not a credential and not patient data: a name the holder of
// this browser already knows.
const WHO_KEY = "healtrics.who";

export type ClinicRole = "clinic_admin" | "doctor" | "nurse" | "front_desk";

export interface Assignment {
  clinic_id: string;
  clinic_name: string;
  role: ClinicRole;
}

export interface ActiveClinic {
  id: string;
  name: string;
  role: ClinicRole;
}

let accessToken: string | null = null;
let activeClinic: ActiveClinic | null = readStoredClinic();

const listeners = new Set<() => void>();

/** Tell every screen that the session changed. */
function announce(): void {
  listeners.forEach((listener) => listener());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}

export function getActiveClinic(): ActiveClinic | null {
  return activeClinic;
}

export function isSignedIn(): boolean {
  return accessToken !== null;
}

export function startSession(tokens: {
  access_token: string;
  refresh_token: string;
  full_name?: string;
}): void {
  accessToken = tokens.access_token;
  sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  if (tokens.full_name) sessionStorage.setItem(WHO_KEY, tokens.full_name);
  announce();
}

/** The signed-in person's name, once they have signed in. */
export function getWho(): string | null {
  try {
    return sessionStorage.getItem(WHO_KEY);
  } catch {
    return null;
  }
}

/** A rotated pair from a refresh. The active clinic does not change. */
export function replaceTokens(tokens: { access_token: string; refresh_token: string }): void {
  accessToken = tokens.access_token;
  sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
}

export function setActiveClinic(clinic: ActiveClinic | null): void {
  activeClinic = clinic;
  if (clinic) {
    sessionStorage.setItem(CLINIC_KEY, JSON.stringify(clinic));
  } else {
    sessionStorage.removeItem(CLINIC_KEY);
  }
  announce();
}

export function endSession(): void {
  accessToken = null;
  activeClinic = null;
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(CLINIC_KEY);
  sessionStorage.removeItem(WHO_KEY);
  announce();
}

function readStoredClinic(): ActiveClinic | null {
  const raw = sessionStorage.getItem(CLINIC_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ActiveClinic;
  } catch {
    // A value this page cannot read is a value it does not use.
    sessionStorage.removeItem(CLINIC_KEY);
    return null;
  }
}
